import GridTable from '../../table/GridTable';
import { ColDef } from 'ag-grid-community';
import { IConsultOrder, useConsultOrderStore } from '../../../store/useConsultOrder';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate, updateUrlWithParams } from '../../../util/util';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import ConfirmDialog from '../../modal/ConfirmDialog';
import { useFranchiseStore } from '../../../store/useFranchise';

const ConsultOrderList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    orders,
    ordersPage,
    getOrderLoading,
    getOrders,
    confirmOrders,
    deleteOrders,
  } = useConsultOrderStore();

  const gridRef = useRef<AgGridReact<IConsultOrder>>(null);
  const { myfranchise, getMyFranchise } = useFranchiseStore();

  useEffect(() => {
    getMyFranchise();
  }, [getMyFranchise]);

  const colDefs: ColDef<IConsultOrder>[] = [
    {
      headerName: '가맹점',
      field: 'franchiseName',
      flex: 2,
    },
    {
      headerName: '이름',
      field: 'name',
      flex: 2,
    },
    {
      headerName: '양/음력',
      field: 'solarType',
      flex: 2,
    },
    {
      headerName: '생년월일',
      field: 'birthday',
      flex: 3,
    },
    {
      headerName: '성별',
      field: 'sexType',
      flex: 1,
    },
    {
      headerName: '상태',
      field: 'status',
      valueFormatter: param => param.value?.description || param.value?.code,
      flex: 2,
    },
    {
      headerName: '생성 날짜',
      field: 'createdAt',
      valueFormatter: param => formatDate(param.value).timestamp,
      flex: 3,
    },
  ];

  const getFilteredData = useCallback(
    (params?: { size?: number }) => {
      const parsedQuery = queryString.parse(location.search);
      const size = params?.size
        ? Number(params.size)
        : parsedQuery.size
        ? Number(parsedQuery.size)
        : 20;

      updateUrlWithParams({ size }, navigate);
      getOrders({
        status: 'WAIT',
        size,
      });
    },
    [location.search, navigate, getOrders]
  );

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const page = parsedQuery.page ? Number(parsedQuery.page) : 0;
    const size = parsedQuery.size ? Number(parsedQuery.size) : 20;

    getOrders({
      status: 'WAIT',
      page,
      size,
    });
  }, [location.search, getOrders]);

  // 주문 확정 모달
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [confirmAmount, setConfirmAmount] = useState<number>(0);

  const showConfirmModal = useCallback(() => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    const idArray = selectedRows?.map(row => row.id);

    if (selectedRows && idArray && idArray.length > 0) {
      const amount = idArray.length * 500;
      setConfirmAmount(amount);
      setIsConfirmOpen(true);
    } else {
      toast.warn('선택된 주문이 없습니다.');
    }
  }, []);

  const onClickConfirm = useCallback(async () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    const idArray = selectedRows?.map(row => row.id);

    if (selectedRows && idArray && idArray.length > 0) {
      await confirmOrders(idArray);
      setIsConfirmOpen(false);
    } else {
      toast.warn('선택된 주문이 없습니다.');
    }
  }, [confirmOrders]);

  // 주문 삭제 모달
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);

  const showDeleteModal = useCallback(() => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    const idArray = selectedRows?.map(row => row.id);

    if (selectedRows && idArray && idArray.length > 0) {
      setIsDeleteOpen(true);
    } else {
      toast.warn('선택된 주문이 없습니다.');
    }
  }, []);

  const onDeleteConfirm = useCallback(async () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    const idArray = selectedRows?.map(row => row.id);

    if (selectedRows && idArray && idArray.length > 0) {
      await deleteOrders(idArray);
      setIsDeleteOpen(false);
      getFilteredData();
    } else {
      toast.warn('선택된 주문이 없습니다.');
    }
  }, [deleteOrders, getFilteredData]);

  const onPageChange = useCallback(
    (page: number) => {
      const parsedQuery = queryString.parse(location.search);
      const size = parseInt(parsedQuery.size as string) || 20;

      updateUrlWithParams({ page: page.toString(), size }, navigate);
      getOrders({
        status: 'WAIT',
        page,
        size,
      });
    },
    [location.search, navigate, getOrders]
  );

  const rowSelection = {
    mode: 'multiRow',
  };

  return (
    <div className="!w-full !h-full !flex !flex-col">
      <div className="!flex !items-center !justify-between !py-4">
        <div className="!text-lg !font-semibold">
          상담 주문 대기 (총 {ordersPage?.totalElements || 0}건)
        </div>
        <div className="!text-sm !text-gray-600">
          {myfranchise && (
            <span>남은 충전금: {myfranchise.chargeAmount?.toLocaleString()}원</span>
          )}
        </div>
      </div>

      <div className="!flex-1 !min-h-0">
        <GridTable<IConsultOrder>
          gridRef={gridRef}
          height="100%"
          isLoading={getOrderLoading}
          rowData={orders}
          columnDefs={colDefs}
          rowSelection={rowSelection}
        />
      </div>

      {/* 페이지네이션 */}
      {ordersPage && (
        <div className="!w-full !mt-6 md:!relative">
          {ordersPage.totalPages > 1 ? (
            <div className="!flex !justify-center !items-center !space-x-1">
              <button
                onClick={() => onPageChange(0)}
                disabled={ordersPage.pageable.pageNumber === 0}
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  className="!w-3 !h-3 !-ml-1"
                />
              </button>

              <button
                onClick={() =>
                  onPageChange(Math.max(0, ordersPage.pageable.pageNumber - 1))
                }
                disabled={ordersPage.pageable.pageNumber === 0}
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
              </button>

              <div className="!flex !space-x-0.5">
                {(() => {
                  const currentPage = ordersPage.pageable.pageNumber;
                  const totalPages = ordersPage.totalPages;
                  let startPage = Math.max(0, currentPage - 4);
                  let endPage = Math.min(totalPages - 1, startPage + 9);

                  if (endPage - startPage < 9) {
                    startPage = Math.max(0, endPage - 9);
                  }

                  return Array.from(
                    { length: endPage - startPage + 1 },
                    (_, i) => startPage + i
                  ).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`!px-2.5 !py-1 !text-sm !rounded !transition-colors !duration-200 ${
                        pageNum === currentPage
                          ? '!bg-blue-600 !text-white'
                          : 'hover:!bg-gray-100 !text-gray-700'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  ));
                })()}
              </div>

              <button
                onClick={() =>
                  onPageChange(
                    Math.min(
                      ordersPage.totalPages - 1,
                      ordersPage.pageable.pageNumber + 1
                    )
                  )
                }
                disabled={
                  ordersPage.pageable.pageNumber === ordersPage.totalPages - 1
                }
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
              </button>

              <button
                onClick={() => onPageChange(ordersPage.totalPages - 1)}
                disabled={
                  ordersPage.pageable.pageNumber === ordersPage.totalPages - 1
                }
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="!w-3 !h-3 !-ml-1"
                />
              </button>
            </div>
          ) : (
            <div className="!h-10"></div>
          )}

          {/* 우측 버튼 영역 */}
          <div className="!flex !gap-2 !justify-end !mt-4 md:!mt-0 md:!absolute md:!right-0 md:!top-1/2 md:!-translate-y-1/2">
            <button
              type="button"
              onClick={showDeleteModal}
              className="!px-4 !py-2 !bg-red-500 hover:!bg-red-600 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
            >
              주문 삭제
            </button>
            <button
              type="button"
              onClick={showConfirmModal}
              className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-600 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
            >
              주문 확정
            </button>
          </div>
        </div>
      )}

      {/* 확정 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={onClickConfirm}
        title="주문 확정 확인"
        message={`선택한 주문을 확정하시겠습니까?
${confirmAmount.toLocaleString()}원이 차감됩니다.`}
        confirmText="확정"
        cancelText="취소"
        type="info"
      />

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={onDeleteConfirm}
        title="주문 삭제 확인"
        message="정말로 이 주문을 삭제하시겠습니까?
삭제된 주문은 복구할 수 없습니다."
        confirmText="삭제"
        cancelText="취소"
        type="danger"
      />
    </div>
  );
};

export default ConsultOrderList;
